import { HttpService } from "data/http-service";
import { Snackbar } from "ui/pages/snackbar";
import { translate } from "ui/i18n";
import { AxiosError } from "axios";
import { FoldersGateway } from "domain/folders/folders.gateway";
import {
  CreateFolder,
  Folder,
  FolderSeance,
  UpdateFolder,
} from "domain/entities/folders.entities";
import {
  folderDtoSchema,
  foldersDtoSchema,
  folderSeancesDtoSchema,
} from "data/folders/folders.dto.schemas";

export class FoldersHttpGateway implements FoldersGateway {
  async getFolders(): Promise<{
    folders: Folder[];
    success: boolean;
  }> {
    return HttpService.get(`folders`)
      .then((response) => {
        if (response.status === 200) {
          const parsedFolders = foldersDtoSchema.safeParse(response.data.data);
          if (parsedFolders.success) {
            return {
              folders: parsedFolders.data,
              success: true,
            };
          } else return { folders: [], success: false };
        } else {
          Snackbar.showError({
            title: translate("ns1:Folders.FindError"),
          });
          return {
            folders: [],
            success: false,
          };
        }
      })
      .catch((error: AxiosError) => {
        Snackbar.showError({
          title: translate("ns1:Folders.FindError"),
        });
        return { folders: [] as Folder[], success: false };
      });
  }

  async createFolder(folder: CreateFolder): Promise<{
    folder: Folder | null;
    success: boolean;
  }> {
    return HttpService.post(`folders`, folder)
      .then((response) => {
        if (response.status === 201 && response.data.id) {
          const parsedFolder = folderDtoSchema.safeParse(response.data);
          if (parsedFolder.success) {
            return {
              folder: parsedFolder.data,
              success: true,
            };
          } else return { folder: null, success: false };
        } else {
          Snackbar.showError({
            title: translate("ns1:Folders.CreationError"),
          });
          return {
            folder: null,
            success: false,
          };
        }
      })
      .catch((error: AxiosError) => {
        Snackbar.showError({
          title: translate("ns1:Folders.CreationError"),
        });
        return { folder: null as Folder, success: false };
      });
  }

  async updateFolder(folder: UpdateFolder): Promise<{
    folder: Folder | null;
    success: boolean;
  }> {
    return HttpService.patch(`folders/${folder.id}`, { label: folder.label })
      .then((response) => {
        if (response.status === 200 && response.data.id) {
          const parsedFolder = folderDtoSchema.safeParse(response.data);
          if (parsedFolder.success) {
            return {
              folder: parsedFolder.data,
              success: true,
            };
          } else return { folder: null, success: false };
        } else {
          Snackbar.showError({
            title: translate("ns1:Folders.UpdateError"),
          });
          return {
            folder: null,
            success: false,
          };
        }
      })
      .catch((error: AxiosError) => {
        Snackbar.showError({
          title: translate("ns1:Folders.UpdateError"),
        });
        return { folder: null as Folder, success: false };
      });
  }

  async getFolderSeances(folderId: number): Promise<{
    seances: FolderSeance[];
    success: boolean;
  }> {
    return HttpService.get(`folders/${folderId}/seances`)
      .then((response) => {
        if (response.status === 200) {
          const parsedSeances = folderSeancesDtoSchema.safeParse(
            response.data.data
          );
          if (parsedSeances.success) {
            return {
              seances: parsedSeances.data,
              success: true,
            };
          } else return { seances: [], success: false };
        } else {
          Snackbar.showError({
            title: translate("ns1:Seances.FindError"),
          });
          return {
            seances: [],
            success: false,
          };
        }
      })
      .catch((error: AxiosError) => {
        Snackbar.showError({
          title: translate("ns1:Seances.FindError"),
        });
        return { seances: [] as FolderSeance[], success: false };
      });
  }

  async deleteFolder(folderId: number): Promise<{
    success: boolean;
  }> {
    return HttpService.delete(`folders/${folderId}`)
      .then((response) => {
        if (response.status === 200) {
          return { success: true };
        } else {
          Snackbar.showError({
            title: translate("ns1:Folders.DeleteError"),
          });
          return {
            success: false,
          };
        }
      })
      .catch((error: AxiosError) => {
        Snackbar.showError({
          title: translate("ns1:Folders.DeleteError"),
        });
        return { success: false };
      });
  }

  async addSeanceToFolder(body: {
    seanceId: number;
    folderId: number;
  }): Promise<{
    success: boolean;
  }> {
    return HttpService.post(`folders/${body.folderId}/seances`, {
      seanceId: body.seanceId,
    })
      .then((response) => {
        if (response.status === 201) {
          return { success: true };
        } else {
          Snackbar.showError({
            title: translate("ns1:Folders.AddToFolderError"),
          });
          return {
            success: false,
          };
        }
      })
      .catch(
        (
          error: AxiosError & {
            response: {
              status: number;
              data: { message: string };
            };
          }
        ) => {
          if (
            error.response?.status === 403 &&
            error.response.data?.message === "Seance already in folder"
          ) {
            Snackbar.showError({
              title: translate("ns1:Folders.SeanceAlreadyInFolder"),
            });
          } else {
            Snackbar.showError({
              title: translate("ns1:Folders.AddToFolderError"),
            });
          }
          return { success: false };
        }
      );
  }

  async removeSeanceFromFolder(body: {
    folderId: number;
    folderSeanceId: number;
  }): Promise<{
    success: boolean;
  }> {
    return HttpService.delete(
      `folders/${body.folderId}/seances/${body.folderSeanceId}`
    )
      .then((response) => {
        if (response.status === 200) {
          return { success: true };
        } else {
          Snackbar.showError({
            title: translate("ns1:Folders.RemoveFromFolderError"),
          });
          return {
            success: false,
          };
        }
      })
      .catch((error: AxiosError) => {
        Snackbar.showError({
          title: translate("ns1:Folders.RemoveFromFolderError"),
        });
        return { success: false };
      });
  }
}
